// n=8 élite (PR-N8-3) — Modelo de VISTA del informe (3 niveles) para la web admin.
// Resuelve el catálogo por el `catalog_version` GUARDADO en la evaluación
// (invariante de N8-1), normaliza el detailed_scores y produce:
//   · pillars[] → categories[] → subs[]  (tabla §6, todas con nota)
//   · radar[]   → 21 ejes (una media de categoría por eje, orden por pilar)
//   · overall + status
// Presentación pura; sin React ni BD. La descarga (app) y la evolución entre
// informes (N8-4) consumen otros módulos.

import {
  CATALOGS,
  CATALOG_VERSION,
  normalizeDetailed,
  overall as overallOf,
  reportStatus,
  type Catalogo,
  type PillarBlock,
  type ReportStatus,
} from './evaluationCatalog';

export interface ReportSub {
  key: string;
  label: string;
  score: number | null;
}
export interface ReportCategory {
  key: string;
  label: string;
  score: number | null; // media de subs (persistida)
  subs: ReportSub[];
}
export interface ReportPillar {
  key: string;
  label: string;
  color: string;
  score: number | null; // media de categorías (persistida)
  comment: string;
  categories: ReportCategory[];
}
export interface RadarAxis {
  key: string; // `${pillar}.${categoria}`
  label: string;
  short: string; // abreviatura EN para móvil/PDF (etiquetas del radar sin recorte)
  value: number | null;
  pillar: string;
  color: string;
}

// PJ-3 / B3 — Abreviaturas EN de las 21 categorías para que la etiqueta del radar
// quepa en móvil y en el SVG del PDF (la web usa la etiqueta completa). Clave =
// categoría del catálogo; fallback a la etiqueta completa si faltara alguna.
export const RADAR_CATEGORY_ABBR: Record<string, string> = {
  short_passing: 'Sht pass',
  long_passing: 'Lng pass',
  receiving: 'Receive',
  ball_carrying: 'Carry',
  ball_mastery: 'Mastery',
  end_product: 'End prod',
  in_possession: 'In poss',
  out_of_possession: 'Out poss',
  transitions: 'Transit',
  positioning: 'Position',
  game_awareness: 'Aware',
  movement: 'Movement',
  decision_making: 'Decision',
  speed: 'Speed',
  strength: 'Strength',
  stamina: 'Stamina',
  abc: 'A-B-C',
  communication: 'Comm',
  confidence: 'Confid',
  concentration: 'Concent',
  professionalism: 'Prof',
  // v3 — los ocho conceptos nuevos. En castellano, porque las etiquetas del v3
  // lo son: una abreviatura inglesa sobre un catálogo en castellano no ayuda a
  // leer el radar, que es para lo único que existe esto. Lo que se recorta es
  // el prefijo MCB/MSB, que es agrupación y no cabe en un vértice.
  mcb_control: 'Control',
  mcb_pase: 'Pase',
  mcb_conduccion: 'Conducc.',
  mcb_proteccion: 'Protecc.',
  mcb_tiro: 'Tiro',
  msb_rti: 'RTI',
  fuerza: 'Fuerza',
  patrones_motores: 'Patrones',
  // Los cuatro de mental (`communication`, `confidence`, `concentration`,
  // `professionalism`) los comparte el v3 con el v2 ahí arriba: significan lo
  // mismo, y esta tabla se indexa por clave de concepto, no por versión.
};
export interface EvaluationReportV2 {
  catalogVersion: number;
  pillars: ReportPillar[];
  radar: RadarAxis[]; // 21 ejes
  overall: number;
  status: ReportStatus;
}

// Invariante N8-1: una evaluación se lee SIEMPRE con su catalog_version guardado.
//
// Hasta este PR, `getCatalog` ignoraba la versión y devolvía el catálogo vigente
// para cualquier `v`. Eso NO degradaba: `normalizeDetailed` parte de
// `buildEmptyDetailed()`, que rellena las 60 subs con DEFAULT_SCORE=5, así que un
// informe de otro catálogo salía COMPLETO, con media 5.0, radar plano y estado
// "Completado" — indistinguible de uno real, y con los datos originales
// descartados en silencio. Fabricar notas es peor que no pintar el informe.
//
// La resolución falla en alto para las versiones que no conoce. Ya no es el
// ÚNICO que protege: desde este PR el catálogo viaja como parámetro a las ocho
// funciones que antes recorrían el global, así que enchufar aquí un catálogo
// histórico —que es lo que hará el v3— deja de abrir el agujero que este throw
// tapaba. Antes, `normalizeDetailed` seguía normalizando contra el vigente dos
// líneas más abajo.
export const UNSUPPORTED_CATALOG_VERSION = 'VERTEX_UNSUPPORTED_CATALOG_VERSION';

export interface UnsupportedCatalogVersionError extends Error {
  code: typeof UNSUPPORTED_CATALOG_VERSION;
  catalogVersion: number;
}

// Discriminamos por `code` y no por `instanceof`: extender Error se transpila
// distinto en Hermes (app) y en Vite (web), y `instanceof` no es fiable ahí.
export function isUnsupportedCatalogVersionError(e: unknown): e is UnsupportedCatalogVersionError {
  return (
    typeof e === 'object' &&
    e !== null &&
    (e as { code?: unknown }).code === UNSUPPORTED_CATALOG_VERSION
  );
}

export const UNSUPPORTED_CATALOG_MESSAGE = 'Informe de un catálogo no soportado';

/** Las versiones que este build sabe leer, para poder DECIRLO en pantalla. */
export function versionesSoportadas(): number[] {
  return Object.keys(CATALOGS)
    .map(Number)
    .sort((a, b) => a - b);
}

export function getCatalog(version: number): Catalogo {
  const catalogo = CATALOGS[version];
  if (!catalogo) {
    const err = new Error(
      `${UNSUPPORTED_CATALOG_MESSAGE}: el informe se puntuó con el catálogo v${version} y esta versión solo sabe leer ${Object.keys(
        CATALOGS
      )
        .map((v) => `v${v}`)
        .join(', ')}.`
    ) as UnsupportedCatalogVersionError;
    err.code = UNSUPPORTED_CATALOG_VERSION;
    err.catalogVersion = version;
    throw err;
  }
  return catalogo;
}

export function buildReportV2Model(
  detailedScores: unknown,
  catalogVersion: number = CATALOG_VERSION
): EvaluationReportV2 {
  const catalogo = getCatalog(catalogVersion);
  // EL MISMO catálogo que se resolvió arriba. Cuando `normalizeDetailed` leía
  // el global, un informe de otra versión salía COMPLETO y con media 5.0:
  // `buildEmptyDetailed` rellena todas las subs con DEFAULT_SCORE y las claves
  // originales se descartaban sin decir nada.
  const ds = normalizeDetailed(detailedScores, catalogo);

  const pillars: ReportPillar[] = catalogo.pilares.map((p) => {
    const block = ds[p.key] as PillarBlock | undefined;
    return {
      key: p.key,
      label: p.label,
      color: p.color,
      score: block?.score ?? null,
      comment: block?.comment ?? '',
      categories: p.categories.map((c) => {
        const cb = block?.categories?.[c.key];
        return {
          key: c.key,
          label: c.label,
          score: cb?.score ?? null,
          subs: c.subs.map((s) => ({
            key: s.key,
            label: s.label,
            score: cb?.subs?.[s.key]?.score ?? null,
          })),
        };
      }),
    };
  });

  const radar: RadarAxis[] = pillars.flatMap((p) =>
    p.categories.map((c) => ({
      key: `${p.key}.${c.key}`,
      label: c.label,
      short: RADAR_CATEGORY_ABBR[c.key] ?? c.label,
      value: c.score,
      pillar: p.key,
      color: p.color,
    }))
  );

  return {
    catalogVersion,
    pillars,
    radar,
    overall: overallOf(ds, catalogo),
    status: reportStatus(ds, catalogo),
  };
}

// Variante tolerante para las LISTAS de informes: un informe de catálogo no
// soportado no puede tumbar la carga de los demás. Devuelve null y el visor
// (EvaluationReportView) cartelea el motivo. Cualquier otro error se propaga:
// aquí solo se absorbe el caso de versión, no los bugs.
export function tryBuildReportV2Model(
  detailedScores: unknown,
  catalogVersion: number = CATALOG_VERSION
): EvaluationReportV2 | null {
  try {
    return buildReportV2Model(detailedScores, catalogVersion);
  } catch (e) {
    if (isUnsupportedCatalogVersionError(e)) return null;
    throw e;
  }
}

// Color por tramo de nota (mismo criterio que el resto del admin).
export function scoreColor(s: number | null): string {
  if (s == null) return '#777777';
  if (s >= 8) return '#4CAF50';
  if (s >= 6) return '#FFF200';
  if (s >= 4) return '#FF9800';
  return '#FF4444';
}

export const STATUS_LABEL: Record<ReportStatus, string> = {
  not_started: 'Sin empezar',
  in_progress: 'En progreso',
  completed: 'Completado',
};
